import React, { useEffect, useState } from 'react';
import { Box, Preloader, Text, useResponsive } from '@common-fe/common-fe';
import styled from 'styled-components';

import { DocumentPagePreview } from '@/components/controls/DropScan/dropScan.types';
import useConvertPdfToPng from '@/utils/hooks/useConvertPdfToPng';

import PdfPasswordPrompt from './PdfPasswordPrompt';

const RoundedImg = styled.img`
  ${({ theme }) => theme.shadows.active};
  border-radius: ${({ theme }) => theme.rounds.container2Round};
`;

interface Props {
  url?: string;
  alt?: string;
  onSetPreviews?: (previews: DocumentPagePreview[]) => void;
  allowPasswordEntry?: boolean;
}

const PdfScanViewer: React.FC<Props> = ({ url, alt, onSetPreviews, allowPasswordEntry }) => {
  const { isMobile } = useResponsive();
  const { pngDocuments, isLoading, isPasswordProtected, isPasswordIncorrect, submitPassword } =
    useConvertPdfToPng({ url });
  const [previews, setPreviews] = useState<DocumentPagePreview[]>([]);

  useEffect(() => {
    if (pngDocuments?.length) {
      const newPreviews = pngDocuments.map(
        (pngDocument): DocumentPagePreview => ({
          ...pngDocument,
          pageRef: React.createRef<HTMLDivElement>(),
        })
      );

      setPreviews(newPreviews);

      if (onSetPreviews) {
        onSetPreviews(newPreviews);
      }
    }
  }, [pngDocuments, onSetPreviews]);

  if (isLoading) {
    return (
      <Box height="60px" justify="center" align="center">
        <Preloader testId="pdf-document-preloader" />
      </Box>
    );
  }

  if (isPasswordProtected) {
    return (
      <Box
        height="453px"
        justify="center"
        align="center"
        width="100%"
        style={isMobile ? undefined : { marginLeft: '-48px' }}
      >
        {allowPasswordEntry ? (
          <PdfPasswordPrompt isIncorrect={!!isPasswordIncorrect} onSubmit={submitPassword} />
        ) : (
          <Text data-testid="pdf-document-password-protected" color="textSecondary">
            This document is password protected.
          </Text>
        )}
      </Box>
    );
  }

  return (
    <Box height={{ min: 'auto' }}>
      {previews.map((pngDocument) => (
        <Box
          key={pngDocument?.id}
          ref={pngDocument.pageRef}
          margin={{ top: 'spacing12' }}
        >
          <RoundedImg
            src={pngDocument?.link}
            alt={alt}
          />
        </Box>
      ))}
    </Box>
  );
};

export default PdfScanViewer;
